import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import LabelValuePair from 'pages/paywall/LabelValuePair'

import { shortDate } from 'helpers/formatters'
import GA4 from 'helpers/ga4'
import { BillingData } from 'types/BillingData'
import { PaywallAcct } from 'types/PaywallAcct'
import { PaywallPlanChange } from 'types/PaywallPlanChange'
import Brand from 'helpers/brand'

const STANDARD_DIAGNOSTICS_MESSAGE = 'You are currently accessing Standard Diagnostics. To upgrade your feature capabilities, check out Diagnostics Premium with a 30-day free trial.'
interface PremiumAccountSummaryProps {
  activeBillingData: BillingData
  activeHehmAccount: boolean
  planChange: PaywallPlanChange | undefined
  planIsDeactivated: boolean
  planIsTrial: boolean
  planType: string | undefined
  premiumAccount: PaywallAcct | undefined
  cancelPlanRenewalClickHandler: () => void
  changeCreditCardInfoClickHandler: () => void
  changePlanTypeClickHandler: () => void
  premiumFeaturesClickHandler: () => void
}

const PremiumAccountSummary = ({
  activeBillingData,
  activeHehmAccount,
  planChange,
  planIsDeactivated,
  planIsTrial,
  planType,
  premiumAccount,
  cancelPlanRenewalClickHandler,
  changeCreditCardInfoClickHandler,
  changePlanTypeClickHandler,
  premiumFeaturesClickHandler
}: PremiumAccountSummaryProps) => {
  const { dealerId } = useParams()
  const navigate = useNavigate()

  const sendGA4Event = (label: string) => {
    GA4.sendEvent({
      action: 'Click Premium Account Settings Option',
      category: 'Account Settings',
      label: `Button: ${label}`
    })
  }

  const onStatementsClick = () => {
    sendGA4Event('Statements')
    navigate(`/paywall/statements/${dealerId}`)
  }

  const planTypeDisplay = () => {
    let value = planType || 'Standard'

    if (planChange) {
      if (planChange.targetPlanId === 'diag_yearly') {
        value = `${planType} (Change to Yearly will occur on ` + shortDate(planChange.effectiveDate) + ')'
      } else if (planChange.targetPlanId === 'diag_monthly') {
        value = `${planType} (Change to Monthly will occur on ` + shortDate(planChange.effectiveDate) + ')'
      } else if (planChange.targetPlanId === null) {
        value = `${planType} (cancellation will take effect on ` + shortDate(planChange.effectiveDate) + ')'
      }
    }

    return value
  }

  return (
    <S.Form $borderColor={color('premiumAccent')} $matchHeight={activeHehmAccount && !!premiumAccount}>
      <S.ProductLogo
        src={premiumAccount ? Brand.premiumProductLogoPath() : Brand.productLogoPath()}
        alt={`${Brand.brandName()} logo`}
        $premiumAccount={!!premiumAccount}
      />
      {(premiumAccount) ? (
        <>
          <S.InnerContainer>
            <S.Explanation>Account Summary</S.Explanation>
            {activeBillingData?.email && <S.LabelValuePair key={activeBillingData?.email} label={'Email'} value={activeBillingData?.email} />}
            <S.LabelValuePair label={'Subscription type'} value={planTypeDisplay()} />
            {!planIsTrial &&
              <>
                {planIsDeactivated &&
                  <>
                    <Button decoration="link" text="Enable account" styles={secondaryButtonStyle} onPress={() => navigate(`/paywall/options/${dealerId}`)} />
                    <S.InactiveLabel>Inactive since {shortDate(premiumAccount?.updatedAt)}</S.InactiveLabel>
                  </>
                }
                {!planIsDeactivated &&
                  <>
                    <S.PlanLinkAndNoteContainer>
                      <Button decoration="link" text={'Change plan type to ' + (planTypeDisplay() === 'Monthly' ? 'yearly' : 'monthly')} styles={secondaryButtonStyle} onPress={changePlanTypeClickHandler} />
                      <S.ActionLinkHelperNote>(changes apply on the next billing cycle)</S.ActionLinkHelperNote>
                    </S.PlanLinkAndNoteContainer>
                    <S.PlanLinkAndNoteContainer>
                      <Button decoration="link" text="Cancel plan renewal" styles={secondaryButtonStyle} onPress={cancelPlanRenewalClickHandler} />
                      <S.ActionLinkHelperNote>(cancellation applies on the next billing cycle)</S.ActionLinkHelperNote>
                    </S.PlanLinkAndNoteContainer>
                  </>
                }
              </>
            }
            {planIsTrial &&
              <Button decoration="link" text="Cancel plan renewal" styles={secondaryButtonStyle} onPress={cancelPlanRenewalClickHandler} />
            }
            <S.Line />
          </S.InnerContainer>
          {!planIsDeactivated &&
            <>
              <S.InnerContainer>
                <S.Explanation>Billing Information</S.Explanation>
                <S.LabelValuePair label={'Address'} value={activeBillingData?.address} />
                {activeBillingData?.city && activeBillingData?.state && activeBillingData?.zip &&
                  <S.AddressSecondLine>
                    {[activeBillingData?.city, activeBillingData?.state, activeBillingData?.zip].join(', ')}
                  </S.AddressSecondLine>
                }
                <Button
                  decoration="link"
                  text="Change credit card information"
                  styles={secondaryButtonStyle}
                  onPress={changeCreditCardInfoClickHandler}
                />
              </S.InnerContainer>
            </>
          }
          {!planIsTrial &&
            <S.InnerContainer>
              <Button decoration="link" text="Statements" styles={secondaryButtonStyle} onPress={onStatementsClick} />
              <S.Line />
            </S.InnerContainer>
          }
          <S.InnerContainer>
            <Button decoration="link" text="What are the features?" styles={premiumFeatureBtn} onPress={premiumFeaturesClickHandler} />
          </S.InnerContainer>
        </>
      ) : (
        <S.InnerContainer>
          <S.Data>{STANDARD_DIAGNOSTICS_MESSAGE}</S.Data>
        </S.InnerContainer>
      )}
    </S.Form>
  )
}

const secondaryButtonStyle: ButtonStyles = {
  button: {
    paddingLeft: 0,
    alignSelf: 'flex-start',
    marginTop: 10
  },
  text: {
    fontSize: 16,
    textTransform: 'none'
  }
}

const premiumFeatureBtn: typeof secondaryButtonStyle = {
  button: [secondaryButtonStyle.button, { paddingRight: 0 }],
  text: secondaryButtonStyle.text
}

export default PremiumAccountSummary

const S = {
  AddressSecondLine: styled.div`
    font-size: 16px;
    color: ${color('premiumText')};
  `,
  ActionLinkHelperNote: styled.span`
    font-size: 12px;
    margin-top: 10px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  Data: styled.div`
    display: flex;
    min-height: 400px;
  `,
  InactiveLabel: styled.div`
    font-size: 12px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  LabelValuePair: styled(LabelValuePair)`
    margin-top: 15px;
  `,
  Line: styled.div`
    border: 1.5px solid ${color('border')};
    width: 100%;
    margin-top: 25px;
    margin-bottom: 25px;
  `,
  Explanation: styled.h2`
    display: flex;
    margin-bottom: 10px;
    text-align: left;
    text-transform: none;
    font-size: 24px;
  `,
  Form: styled.div<{ $borderColor?: string; $matchHeight?: boolean }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-content: flex-start;
    border-radius: 10px;
    background-color: ${color('billingAddressBox')};
    padding: 15px;
    max-width: fit-content;
    border: 1px solid ${(props) => props.$borderColor || 'transparent'};
    ${(props) => props.$matchHeight && 'height: 670px;'}
    width: 425px;
    @media ${screenSize.medium} {
        width: 350px;
        height: 100%;
    }
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    width: 96%;
    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  PlanLinkAndNoteContainer: styled.div`
    display: flex;
    align-items: center;
  `,
  ProductLogo: styled.img<{ $premiumAccount?: boolean }>`
    width: ${(props) => (props.$premiumAccount ? '360px' : '225px')};
    margin-bottom: 2rem;
    @media ${screenSize.small}, ${screenSize.medium} {
      max-width: 290px;
    }
  `
}
